import {
  Children,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState
} from "react";
import { classNames, useIsMounted } from "@ct-react/core";
import "./slidable-box.scss";

type ValidChildren = ReactElement<typeof SlidableItem>[];

type ItemProps = PropsWithChildren & {
  className?: string;
  active?: boolean;
}

type Props = {
  className?: string;
  displayedIndex?: number;
  children: ValidChildren;
  animated?: boolean;
}

export const SlidableItem = ({ className, active = false, children }: ItemProps) => {

  const wrapperClass = classNames("si-item", className, { active });

  return (
    <div className={wrapperClass}>
      {children}
    </div>);

};

const SlidableBox = ({ className, displayedIndex = 1, children, animated = true }: Props) => {

  const isMounted = useIsMounted();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [ wrapperHeight, setWrapperHeight ] = useState<number | undefined>(undefined);
  const [ active, setActive ] = useState<number>(displayedIndex - 1);

  useEffect(() => {
    if (!isMounted) return;
    setActive(displayedIndex - 1);
  }, [ displayedIndex ]);

  useEffect(() => {
    const activeChild = wrapperRef.current!.children[active] as HTMLElement | undefined;
    if (!activeChild) return;
    wrapperRef.current!.scroll({
      left: wrapperRef.current!.clientWidth * active,
      behavior: isMounted ? "smooth" : "instant"
    });
    const observer = new ResizeObserver(() => setWrapperHeight(activeChild.scrollHeight));
    observer.observe(activeChild);
    return () => { observer.disconnect(); };
  }, [ active ]);

  const wrapperClasses = classNames("slidable-box", className, { animated });

  return (
    <div ref={wrapperRef}
         className={wrapperClasses}
         style={{ ...!!wrapperHeight && { height: wrapperHeight } }}>
      {Children.toArray(children).filter(isValidElement).map((child, ref) =>
        cloneElement(child, { ref, active: ref === active } as Partial<ItemProps>))}
    </div>);

};

export default SlidableBox;
